import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Heading from 'ui/modules/heading'
import Filters from '../search/common/filters'
import Search from 'ui/modules/search'
import TitleNavigation from 'ui/modules/titleNavigation'
import Typography from '@material-ui/core/Typography'
import NewSearch from '../search/common/newSearch'
import Actions from './actions'
import CheckboxList from 'ui/modules/lists/checkboxList'
import Pagination from 'ui/components/pagination'
import EmptyStateManager from 'containers/EmptyStateManager'
import useTranslation from 'hooks/useTranslations'

const SearchPage = props => {
  const {
    isLoading,
    genericError,
    children,
    totalVendors,
    totalPages,
    currentPage,
    goToPage,
    handleBulkSelect,
    selectedCount,
    errorType,
    handleAddVendor,
    searchOptions,
    getSearchOptions,
    clearSearchOptions,
    onSearch,
    onSaveSearch,
    filters,
    setFilters,
    searchConfig,
    initialSearchParams,
    canSave,
    onAdvancedSearch,
    title,
    onCSVExport,
    searchValue,
    setSearchValue,
    onConfigChange,
  } = props

  const t = useTranslation()

  const selectedStatus = useMemo(() => {
    if (selectedCount === 0) return 'none'
    if (selectedCount === totalVendors) return 'full'
    return 'partial'
  }, [selectedCount, totalVendors])

  const searchFilterOptions = useCallback(options => options, [])

  return (
    <>
      <Box mb={3}>
        <TitleNavigation
          page={t('home:saved-searches')}
          goTo="/app/searches#results"
        />
        <Typography variant="h1">{title}</Typography>
      </Box>

      <NewSearch
        hasButton={true}
        searchConfig={searchConfig}
        searchOptions={searchOptions}
        getSearchOptions={getSearchOptions}
        clearSearchOptions={clearSearchOptions}
        genericError={genericError}
        onSearch={onAdvancedSearch}
        initialValues={initialSearchParams}
        onConfigChange={onConfigChange}
      />
      <Heading
        type="vendors"
        as="h2"
        title={t('search:search-results')}
        number={totalVendors}
      />
      <Search
        placeholder={t('search:search-placeholder')}
        searchOptions={searchOptions}
        filterOptions={searchFilterOptions}
        getSearchOptions={getSearchOptions}
        clearSearchOptions={clearSearchOptions}
        onSearch={onSearch}
        value={searchValue}
        setValue={setSearchValue}
        noOptionsText={t('search:no-options')}
      />
      <Filters filters={filters} setFilters={setFilters} />
      <Box display="flex" flexDirection="column">
        {!isLoading && errorType.type === 'noResultsSearchDetail' ? (
          <EmptyStateManager type={errorType.type} action={errorType.action} />
        ) : (
          <>
            {errorType.type === 'noResultsFilter' ? (
              <EmptyStateManager
                type={errorType.type}
                action={errorType.action}
              />
            ) : (
              <>
                <CheckboxList isLoading={isLoading} hasCheckbox={false}>
                  {children}
                </CheckboxList>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  goToPage={goToPage}
                  showFirstButton
                  showLastButton
                />
              </>
            )}
          </>
        )}
        <Actions
          status={selectedStatus}
          onBulkSelect={handleBulkSelect}
          canDelete={!selectedCount}
          onAddVendor={handleAddVendor}
          onSaveSearch={onSaveSearch}
          canSave={canSave}
          canDownload={totalPages > 0}
          onCSVExport={onCSVExport}
        />
      </Box>
    </>
  )
}

SearchPage.propTypes = {
  isLoading: PropTypes.bool,
  totalVendors: PropTypes.number,
  handleBulkSelect: PropTypes.func,
  selectedCount: PropTypes.number,
  errorType: PropTypes.object,
  onSaveSearch: PropTypes.func,
  filters: PropTypes.object,
  setFilters: PropTypes.func,
  searchConfig: PropTypes.object,
  canSave: PropTypes.bool,
  onAdvancedSearch: PropTypes.func,
  title: PropTypes.string,
  onCSVExport: PropTypes.func,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
  onConfigChange: PropTypes.func,
}

export default SearchPage
